<template>
  <div class="chess-desktop">
    <chess-dates-slider
      v-model:scroll-progress="scrollProgress"
      class="chess-desktop__dates"
      :period="props.period"
      :loading="props.loading"
      @click:tick="handleTickClick"
    />

    <chess-loader-desktop v-if="props.loading" />

    <template v-else>
      <e-expansion-panels
        v-if="props.accordion"
        :model-value="props.accordionMultiple ? Array.from(Array(props.items.length).keys()) : [0]"
        :multiple="props.accordionMultiple"
        class="chess-desktop__panels"
      >
        <e-expansion-panel v-for="(item, itemIndex) in props.items" :key="itemIndex">
          <template #title> {{ item.title }}</template>
          <chess-tables
            ref="tablesRef"
            v-model:scroll-progress="scrollProgress"
            :period="props.period"
            :tables="item.tables"
            :is-edit="isEdit"
            :chess-dates-available-from="props.chessDatesAvailableFrom"
            @select="emit('select', $event)"
          />
        </e-expansion-panel>
      </e-expansion-panels>

      <div v-else class="chess-desktop__panels">
        <div v-for="(item, itemIndex) in props.items" :key="itemIndex">
          <div v-if="item.title" class="paragraph-l mb-2">
            <b>{{ item.title }}</b>
          </div>
          <chess-tables
            ref="tablesRef"
            v-model:scroll-progress="scrollProgress"
            :period="props.period"
            :tables="item.tables"
            :is-edit="isEdit"
            :chess-dates-available-from="props.chessDatesAvailableFrom"
            @select="emit('select', $event)"
          >
            <template v-if="slots.tooltip" #tooltip="{ item }">
              <slot name="tooltip" :item="item" />
            </template>
          </chess-tables>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts" generic="T">
import { ref, useTemplateRef } from 'vue';
import type { ChessDateCellType, ChessItemType, ChessTableRowType } from '@/core/components/interface/Chess/Chess.type';
import type { RangeOfDateOnly } from '@/graphql/generated';
import ChessDatesSlider from '@/core/components/interface/Chess/ChessDatesSlider.vue';
import ChessLoaderDesktop from '@/core/components/interface/Chess/ChessLoaderDesktop.vue';
import ChessTables from '@/core/components/interface/Chess/ChessTables.vue';
import { EExpansionPanel, EExpansionPanels } from '@/core/components/ui';

const props = defineProps<{
  period?: RangeOfDateOnly;
  items: ChessItemType<T>[];
  isEdit?: boolean;
  accordion?: boolean;
  accordionMultiple?: boolean;
  loading?: boolean;
  chessDatesAvailableFrom?: string; // Date in format YYYY-MM-DD
}>();

const emit = defineEmits<{
  (event: 'select', value: ChessDateCellType<T>[]): void;
}>();

const slots = defineSlots<{
  tooltip?: (props: { item: ChessTableRowType<T> }) => void;
}>();

const tablesRef = useTemplateRef('tablesRef');
const scrollProgress = ref(0);

const scrollTo = (date: string) => {
  if (!Array.isArray(tablesRef.value)) return;

  tablesRef.value.forEach((tableRef) => {
    tableRef?.scrollTo(date);
  });
};

const handleTickClick = (date: string) => {
  scrollTo(date);
};

defineExpose({
  unselectAllCells: () => {
    if (!Array.isArray(tablesRef.value)) return;

    tablesRef.value.forEach((tableRef) => {
      tableRef?.unselectAllCells();
    });
  },
  scrollTo,
});
</script>

<style scoped lang="scss">
.chess-desktop {
  padding-top: 8px;

  &__dates {
    margin-bottom: 32px;
  }

  &__panels {
    display: grid;

    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
